import React, { useCallback } from 'react'

import Link, { LinkProps } from './index'
import usePageMeasurements from '../../hooks/usePageMeasurements'
import useEvents from '../../hooks/useEvents'

interface Props extends Omit<LinkProps, 'to' | 'href'> {
  hash: string
}

/**
 * JumpLink is a link that scrolls to another part of the page. Because this app
 * has a sticky floating header, we override the browsers default scrolling
 * behavior to offset the scroll so the element shows up under that header.
 */
const JumpLink: React.FC<Props> = ({ hash, ...props }) => {
  const {
    header: [, { height: headerHeight }],
  } = usePageMeasurements()
  const sendEvent = useEvents()
  const { onClick: propsOnClick } = props

  const onClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    (event) => {
      if (propsOnClick) {
        propsOnClick(event)
      }

      const targetElement = document.getElementById(hash)

      if (!targetElement) {
        return
      }

      const offsetPosition =
        // Take element's position on the page
        targetElement.getBoundingClientRect().top -
        // Subtract floating header height
        headerHeight -
        // Give the link being jumped to about 1em of space
        16 +
        // Add the page y offset to account for scrolling
        window.pageYOffset

      event.preventDefault()
      window.scrollTo({
        top: offsetPosition,
        // https://web.dev/prefers-reduced-motion/
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
      })
      // Set the URLs hash without triggering scrolling
      window.history.replaceState({}, '', `#${hash}`)
      sendEvent({
        event: 'Jump Link Clicked',
        variables: { hash },
      })
    },
    [hash, headerHeight, sendEvent, propsOnClick]
  )

  return <Link href={`#${hash}`} {...props} onClick={onClick} />
}

export default JumpLink
